import { useEffect, useState } from 'react';
import { api, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Star, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

function Stars({ value, onChange, size = 18, interactive = false }) {
  return (
    <div className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          type="button"
          onClick={() => interactive && onChange?.(n)}
          disabled={!interactive}
          className={interactive ? 'cursor-pointer' : 'cursor-default'}
          data-testid={interactive ? `review-star-${n}` : undefined}
        >
          <Star
            size={size}
            strokeWidth={1.5}
            className={n <= value ? 'fill-amber-500 text-amber-500' : 'text-muted-foreground/50'}
          />
        </button>
      ))}
    </div>
  );
}

/**
 * Sezione recensioni per il profilo di un proprietario.
 * - Carica stats + lista
 * - Permette al visitatore autenticato (che non è il target) di lasciare/modificare la propria review
 * - Mostra errore chiaro se non ha mai contattato il proprietario
 */
export default function OwnerReviewsSection({ ownerId, ownerName }) {
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], stats: { count: 0, avg: 0 } });
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get(`/users/${ownerId}/reviews`).then(r => setData(r.data || { items: [], stats: { count: 0, avg: 0 } })).catch(() => {});

  useEffect(() => { if (ownerId) load(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerId]);

  const myReview = user ? data.items.find(r => r.reviewer_id === user.user_id) : null;
  const canSelfReview = user && user.user_id !== ownerId;

  const openEditor = () => {
    if (myReview) { setRating(myReview.rating); setText(myReview.text); }
    else { setRating(5); setText(''); }
    setOpen(true);
  };

  const submit = async () => {
    if (text.trim().length < 10) { toast.error('Scrivi almeno 10 caratteri'); return; }
    setBusy(true);
    try {
      if (myReview) {
        await api.put(`/reviews/${myReview.review_id}`, { target_user_id: ownerId, rating, text });
        toast.success('Recensione aggiornata');
      } else {
        await api.post('/reviews', { target_user_id: ownerId, rating, text });
        toast.success('Grazie per la tua recensione');
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!myReview) return;
    if (!window.confirm('Vuoi davvero rimuovere la tua recensione?')) return;
    try {
      await api.delete(`/reviews/${myReview.review_id}`);
      toast('Recensione rimossa');
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const { avg, count } = data.stats;

  return (
    <section data-testid="owner-reviews" className="mt-10">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-5">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Opinioni degli utenti</div>
          <h2 className="font-display text-2xl md:text-3xl tracking-tighter mt-1">
            {count > 0 ? <>{avg} <Star size={20} className="inline mb-1 fill-amber-500 text-amber-500"/> · {count} {count === 1 ? 'recensione' : 'recensioni'}</>
                       : <>Ancora nessuna recensione</>}
          </h2>
        </div>
        {canSelfReview && (
          <Button onClick={openEditor} data-testid="review-open-btn" variant={myReview ? 'outline' : 'default'} className="rounded-full">
            {myReview ? <><Pencil size={13} className="mr-2"/> Modifica la tua recensione</> : 'Lascia una recensione'}
          </Button>
        )}
      </div>

      {data.items.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nessuno ha ancora recensito {ownerName || 'questo utente'}. Dopo averlo contattato tramite AgriNexus potrai essere il primo.</p>
      ) : (
        <ul className="space-y-5">
          {data.items.map(r => (
            <li key={r.review_id} data-testid={`review-${r.review_id}`} className="rounded-2xl border border-border bg-card p-5">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <Stars value={r.rating} size={14}/>
                  <div className="mt-1 text-sm font-medium">{r.reviewer_name}</div>
                  <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
                    {new Date(r.created_at).toLocaleDateString('it-IT', { year: 'numeric', month: 'long' })}
                    {r.updated_at && r.updated_at !== r.created_at && ' · modificata'}
                  </div>
                </div>
              </div>
              <p className="mt-3 text-sm leading-relaxed whitespace-pre-line">{r.text}</p>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{myReview ? 'Modifica recensione' : `Recensisci ${ownerName || 'questo utente'}`}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Quante stelle?</div>
              <Stars value={rating} onChange={setRating} interactive size={28}/>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Racconta la tua esperienza</div>
              <Textarea value={text} onChange={e => setText(e.target.value)} rows={5}
                        maxLength={1500} data-testid="review-text"
                        placeholder="È stato puntuale, le info sul terreno erano corrette, ha risposto alle mie domande…"/>
              <div className="mt-1 text-[11px] text-muted-foreground text-right">{text.length}/1500</div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            {myReview && (
              <Button variant="ghost" onClick={remove} className="mr-auto text-destructive">
                <Trash2 size={13} className="mr-2"/> Elimina
              </Button>
            )}
            <Button variant="ghost" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={submit} disabled={busy || text.trim().length < 10} data-testid="review-submit">
              {busy ? 'Invio…' : myReview ? 'Aggiorna' : 'Pubblica'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
